// Builds a shareable, always-https link to a project Page
// (CYBORG-308 — "shared page link opens the desktop app if installed, else
// the website"). A shareable link must be openable by ANYONE it's pasted to
// (a web browser, another user, Slack, …), so it can never be
// `window.location.origin` verbatim: inside the Electron desktop shell that
// origin is the custom scheme `cyborg://app` (see notifications/+page.svelte
// and web-push.ts for the same origin quirk), which is meaningless outside
// the app.
//
// CANONICAL_APP_ORIGIN is the UI's one copy of the production web host; the invite-link
// copy button and "Manage on web" take it through resolveShareOrigin() too.
// It matches the server's own default
// (`process.env.CYBORG_APP_URL ?? "https://app.cyborg7.com"` in
// relay-standalone.ts:287, used as INVITE_BASE_URL) and the relay's CORS
// comment calling `https://app.cyborg7.com` "the production web host"
// (relay-standalone.ts:13492-13494). relay.cyborg7.com is a DIFFERENT host —
// the desktop app's WS/API endpoint (see core/relay-config.ts) — not the web
// UI's origin.
const CANONICAL_APP_ORIGIN = "https://app.cyborg7.com";

/**
 * The origin to use for ANY shareable link this app builds — not just pages.
 * Extracted out of pageShareUrl (below) so message-link.ts (Copy-link on a
 * message) reuses this ONE rule instead of growing a second copy — the
 * mirror-image bug already happened once elsewhere: the native iOS port of
 * the message-link feature resolved its link against the relay/WS host
 * instead, and every link it produced landed outside the app's Universal
 * Link entitlement.
 *
 * Rule: on a real web origin — `window.location.origin` under `https:` (the
 * hosted website, including a self-hosted deployment at a different domain)
 * OR `http:` (local dev, plain-http self-host) — that origin IS already the
 * canonical, shareable one, so we use it as-is rather than overriding to the
 * cyborg7.com default. Everything else falls back to CANONICAL_APP_ORIGIN:
 * the Electron desktop shell's `cyborg://app` custom scheme (see
 * notifications/+page.svelte and web-push.ts for the same origin quirk), the
 * Tauri Android/iOS shells wrapping this identical Svelte build, and
 * SSR/build where there's no `window` at all.
 */
export function resolveShareOrigin(): string {
  return typeof window !== "undefined" &&
    (window.location.protocol === "https:" || window.location.protocol === "http:")
    ? window.location.origin
    : CANONICAL_APP_ORIGIN;
}

/** `/workspace/<wsId>/tasks/<projectId>/pages/<pageId>` — same-origin path to a page. */
export function pagePath(wsId: string, projectId: string, pageId: string): string {
  return `/workspace/${wsId}/tasks/${projectId}/pages/${pageId}`;
}

/** Full shareable URL for a page: resolveShareOrigin() + pagePath(). */
export function pageShareUrl(wsId: string, projectId: string, pageId: string): string {
  return `${resolveShareOrigin()}${pagePath(wsId, projectId, pageId)}`;
}
